import FileUpload from '@/components/FileUpload';
import OncolensToast from '@/components/OncolensToast';
import Banner from '@oncolens/oncolens-ui/lib/components/Common/Banner/Banner';
import { BannerType } from '@oncolens/oncolens-ui/lib/components/Common/Banner/Banner.helpers';
import Button from '@oncolens/oncolens-ui/lib/components/FormControls/Button';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import RadioButtons from '@oncolens/oncolens-ui/lib/components/FormControls/RadioButtons';
import TextAreaInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextAreaInput';
import TextInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextInput';
import FormField from '@oncolens/oncolens-ui/lib/libraries/FormField';
import useFormStore from '@oncolens/oncolens-ui/lib/libraries/useFormStore';
import React, { useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useNavigate } from 'react-router';
import * as yup from 'yup';
import shallow from 'zustand/shallow';
import { useCreateSsoConnection } from '../api/createSsoConnection';
import { useDeleteSsoConnection } from '../api/deleteSsoConnection';
import { useGetSsoConnection } from '../api/getSsoConnection';
import { useUpdateSsoConnection } from '../api/updateSsoConnection';
import { SsoConnectionForManipulationDto } from '../types';

type SsoConnectionFormProps = {
  organizationId: string | undefined;
};

const ssoPropsBoolOptions = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
];

const getSsoPropsBoolToString = (value?: boolean) => {
  switch (value) {
    case true:
      return 'Yes';
    case false:
      return 'No';
    default:
      return '';
  }
};

const getSsoPropsBoolFromString = (value?: string) => {
  switch (value) {
    case 'Yes':
      return true;
    case 'No':
      return false;
    default:
      return null;
  }
};

const getStatusText = (value?: string) => {
  switch (value) {
    case 'Active':
      return 'This Sso Connection is active in Auth0.';
    case 'Active With Defaults':
      return 'This Sso Connection is active in Auth0 with default values. Values must be updated to be able to use this connection. Values with defaults may include Sign In Url, X509 Signing Certificate, & Identity Provider Domains.';
    case 'Archived':
      return 'This Sso Connection is archived and has been deleted from Auth0. You must create a new connection with a different name for this organization.';
    default:
      return '';
  }
};

const getBannerType = (value?: string) => {
  switch (value) {
    case 'Active With Defaults':
      return BannerType.Warning;
    default:
      return BannerType.Default;
  }
};

const ssoConnectionSchema = yup.object<
  Record<keyof SsoConnectionForManipulationDto, yup.AnySchema>
>({
  file: yup
    .object()
    .shape({
      fileName: yup.string().nullable(),
      certificate: yup.string().nullable(),
      isNewFile: yup.boolean().required(),
    })
    .nullable(),
  connectionName: yup.string().required(),
  signInUrl: yup.string().nullable(),
  signOutUrl: yup.string().nullable(),
  enableSignOut: yup.boolean().required(),
  enableIdpInitiation: yup.boolean().required(),
  organizationId: yup.string().required(),
  domainAliases: yup.string().nullable(),
});

const defaultValues = {
  file: {
    fileName: undefined,
    certificate: undefined,
    isNewFile: false,
  },
  connectionName: '',
  signInUrl: undefined,
  signOutUrl: undefined,
  enableSignOut: false,
  enableIdpInitiation: false,
  domainAliases: '',
};

export const SsoConnectionForm = ({ organizationId }: SsoConnectionFormProps) => {
  const { data: ssoConnection } = useGetSsoConnection(organizationId, {
    onSuccess: (data) =>
      updateStore((x) => {
        Object.assign(x, data);
      }),
  });

  const formStore = useFormStore({ organizationId, ...defaultValues }, ssoConnectionSchema);
  const [updateStore, isValid, reset] = formStore(
    (x) => [x.updateStore, x.isValid, x.reset],
    shallow,
  );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const navigate = useNavigate();
  const updateSsoConnectionMutation = useUpdateSsoConnection(ssoConnection?.id);
  const createSsoConnection = useCreateSsoConnection(organizationId!);
  const deleteSsoConnection = useDeleteSsoConnection(organizationId!);

  const onSave = () => {
    const store = formStore.getState();

    if (!store.isValid) return;

    setSaving(true);
    const dataToSave = store.getValues();

    if (ssoConnection) {
      updateSsoConnectionMutation.mutate(dataToSave, {
        onSuccess: () => OncolensToast.success('Sso Connection updated'),
        onError: () => OncolensToast.error('Error updating Sso Connection'),
        onSettled: () => setSaving(false),
      });
    } else {
      createSsoConnection.mutate(dataToSave, {
        onSuccess: () => OncolensToast.success('Sso Connection created'),
        onError: () => OncolensToast.error('Error creating Sso Connection'),
        onSettled: () => setSaving(false),
      });
    }
  };

  const onDelete = () => {
    if (!ssoConnection || !ssoConnection.id) return;
    setDeleting(true);

    deleteSsoConnection
      .mutateAsync(ssoConnection.id)
      .then(() => {
        reset();
        OncolensToast.success('Sso Connection deleted');
        setDeleting(false);
      })
      .catch(() => {
        OncolensToast.error('Sso Connection deletion failed.');
      });
  };

  return (
    <div className="rounded-md bg-white p-3 min-h-[32rem] ring-opacity-60 focus:outline-none">
      {ssoConnection && (
        <Banner
          type={getBannerType(ssoConnection.status)}
          className="col-span-3 my-4 font-semibold">
          {getStatusText(ssoConnection.status)}
        </Banner>
      )}
      <div className="grid grid-cols-1 mb-4 gap-y-6 sm:grid-cols-6 sm:gap-x-6">
        <div className="grid grid-cols-2 col-span-2 md:col-span-3 gap-x-4">
          <FormField name="connectionName" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <div className="col-span-3 mb-4">
                <TextInput
                  id="connectionName"
                  type="text"
                  htmlName="connectionName"
                  label="Connection Name"
                  error={errorMessage}
                  required={true}
                  value={value}
                  onChange={onChange}
                  wrapperProps={{ className: 'col-span-6 md:col-span-3' }}
                  placeholder="CareWell Hospital"
                  disabled={!!ssoConnection}
                />
                <p className="mt-2 text-sm italic">
                  This is a logical identifier of the connection.{' '}
                  <strong>This cannot be changed.</strong>
                </p>
              </div>
            )}
          </FormField>
          <FormField name="signInUrl" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <div className="col-span-3 mb-4">
                <TextInput
                  id="signInUrl"
                  type="text"
                  htmlName="signInUrl"
                  label="Sign In Url"
                  error={errorMessage}
                  value={value}
                  onChange={onChange}
                  placeholder="https://example.com/saml/login"
                  wrapperProps={{ className: 'col-span-6 md:col-span-3' }}
                />
                <p className="mt-2 text-sm italic">
                  When empty this field defaults to a placeholder value.
                </p>
              </div>
            )}
          </FormField>
          <FormField name="file" store={formStore}>
            {({ value, onChange }) => (
              <div className="col-span-3 mb-4">
                <FileUpload
                  id="file"
                  label="X509 Signing Certificate"
                  htmlName="file"
                  value={value}
                  onChange={onChange}
                  wrapperProps={{ className: 'col-span-6 md:col-span-3' }}
                />
                <p className="mt-1 text-sm italic">
                  SAMLP server public key encoded in PEM or CER format.
                </p>
              </div>
            )}
          </FormField>
          <div className="grid grid-cols-2 col-span-2 mb-2 md:col-span-3 gap-x-4">
            <FormField name="enableSignOut" store={formStore}>
              {({ value, onChange }) => (
                <RadioButtons
                  label="Enable Sign Out"
                  value={getSsoPropsBoolToString(value)}
                  onChange={(val) => onChange(getSsoPropsBoolFromString(val))}
                  options={ssoPropsBoolOptions}
                />
              )}
            </FormField>
            <FormField name="enableIdpInitiation" store={formStore}>
              {({ value, onChange }) => (
                <RadioButtons
                  label="Enable Idp Initiated Sign In"
                  value={getSsoPropsBoolToString(value)}
                  onChange={(val) => onChange(getSsoPropsBoolFromString(val))}
                  options={ssoPropsBoolOptions}
                />
              )}
            </FormField>
          </div>
          <FormField name="signOutUrl" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <div className="col-span-3">
                <TextInput
                  id="signOutUrl"
                  type="text"
                  htmlName="signOutUrl"
                  label="Sign Out Url"
                  error={errorMessage}
                  value={value}
                  onChange={onChange}
                  placeholder="https://example.com/saml/logout"
                  wrapperProps={{ className: 'col-span-6 md:col-span-3' }}
                />
                <p className="mt-2 text-sm italic">
                  When empty this field defaults to sign in url.
                </p>
              </div>
            )}
          </FormField>
        </div>
        <div className="grid grid-cols-2 col-span-2 md:col-span-3 gap-x-4">
          <FormField name="domainAliases" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <div className="col-span-3">
                <p className="text-sm font-semibold text-gray-700">
                  Identity Provider Domains: Login Experience Customization
                </p>
                <p className="mt-4 text-sm">
                  Configure how you want users to log in with this connection.
                </p>
                <p className="mt-4 text-sm">
                  When using Lock.js in your Universal Login page, the user&apos;s email domain will
                  be compared with the identity provider domains. If there is a match, users will be
                  redirected to the identity provider. If there is no match, users will be prompted
                  to enter their password.
                </p>
                <TextAreaInput
                  id="domainAliases"
                  label=""
                  value={value}
                  onChange={onChange}
                  htmlName="domainAliases"
                  rows={8}
                  wrapperProps={{ className: 'col-span-6 md:col-span-3' }}
                  error={errorMessage}
                  placeholder="example.com, example.org"
                />
                <p className="mt-4 text-sm italic">
                  One or multiple, comma seperated list of the domains that can be authenticated in
                  the Identity Provider.
                </p>
              </div>
            )}
          </FormField>
        </div>
      </div>
      <div className="flex items-center w-full">
        <Button className="w-auto px-6 mr-3" buttonColor="gray" onClick={() => navigate(-1)}>
          Back
        </Button>
        {ssoConnection && (
          <ButtonWithLoading
            loading={deleting}
            className="w-auto px-6 ml-auto mr-3"
            buttonColor="red"
            onClick={onDelete}>
            Delete
          </ButtonWithLoading>
        )}
        <ButtonWithLoading
          loading={saving}
          className="w-auto px-6 mr-3"
          buttonColor="blue"
          onClick={onSave}
          disabled={!isValid}>
          Save
        </ButtonWithLoading>
      </div>
    </div>
  );
};
